import React, { useState } from 'react';
import Placeholder from '@atlaskit/react-ufo/placeholder';
import { fg } from '@atlassian/jira-feature-gating';
import type { MainIssueAggQueryRelayFragment } from '@atlassian/jira-issue-fetch-services-common/src/services/issue-agg-data/main.tsx';
import type { CommentsPaginationQuery$variables as CommentsPaginationParams } from '@atlassian/jira-relay/src/__generated__/CommentsPaginationQuery.graphql';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import Comments from './comments-view.tsx';

type Props = {
	rootRelayFragment?: MainIssueAggQueryRelayFragment | null;
};

let nextCommentsFetcher: (commentsPaginationParams: CommentsPaginationParams) => void;
let commentsPaginationParams: CommentsPaginationParams;

const CommentsViewWrapper = ({ rootRelayFragment }: Props) => {
	const [loadFetcher, setLoadFetcher] = useState(false);

	// called only once when the refetcher component loads for the first time
	const setRefetchInstance = (
		_nextCommentsFetcher: (commentsPaginationParams: CommentsPaginationParams) => void,
	) => {
		if (!nextCommentsFetcher) {
			nextCommentsFetcher = _nextCommentsFetcher;
			// as fetcher was just initialized, manually make the first call
			fetchNextComments();
		}
	};

	// called from comments-view every time the load more button is clicked
	const getNewComments = (_commentsPaginationParams: CommentsPaginationParams) => {
		commentsPaginationParams = _commentsPaginationParams;
		fetchNextComments();

		if (!loadFetcher) {
			setLoadFetcher(true);
		}
	};

	const fetchNextComments = () => {
		if (nextCommentsFetcher && commentsPaginationParams) {
			nextCommentsFetcher(commentsPaginationParams);
		}
	};

	return (
		<>
			<Comments
				getNewComments={getNewComments}
				{...(fg('smart-replies-system-setting') ? { rootRelayFragment } : {})}
			/>
			{loadFetcher && (
				<Placeholder name="CommentsFetcher" fallback={null}>
					<LazyCommentsFetcher setRefetchInstance={setRefetchInstance} />
				</Placeholder>
			)}
		</>
	);
};

const LazyCommentsFetcher = lazyForPaint(
	() => import(/* webpackChunkName: "async-comments-fetcher" */ './ui/comments-fetcher.tsx'),
);

export default CommentsViewWrapper;
